<template>
  <LoadingSpinner v-if="isLoading || companiesLoading" />

  <div v-else >
    <PageHeader title="Add Subcontractor" />

    <div class="shadow p-5 mt-2 mb-5 bg-white rounded">
      <SubcontractorForm
        :sub="sub"
        :trades="trades"
        :companies="companies"
        :save-function="createSub"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Subcontractor } from '@/models/subcontractor'
import SubcontractorService from '@/services/SubcontractorService'
import LaborService from '@/services/LaborService'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import PageHeader from '@/components/app/AppPageHeader.vue'
import SubcontractorForm from './components/SubcontractorForm.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { useHead } from '@unhead/vue'
import { useMutation } from '@tanstack/vue-query'
import { useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'

useHead({ title: 'Add Subcontractor' })

const subService = new SubcontractorService()
const { notify } = useNotification()
const $router = useRouter()
const sub = new Subcontractor()
const laborService = new LaborService()
const queryClient = useQueryClient()

const { data: trades, isLoading } = useQuery({
  queryKey: ['trades'],
  queryFn: () => laborService.all().then((labors) => labors.map((labor) => ({ value: labor.id, label: labor.name })))
})

const { data:companies, isLoading: companiesLoading } = useQuery({
  queryKey: ['companies'],
  queryFn: () => subService.getAllCompanies().then((companies) => companies.map((company) => ({ value: company.id, label: company.name })))
})


const { mutateAsync: createSub } = useMutation({
  mutationFn: (sub: Subcontractor) => subService.create(sub),
  onSuccess(newSub) {
    queryClient.setQueryData(['subcontractor', newSub.id], newSub)
    queryClient.invalidateQueries({ queryKey: ['subcontractors'] })
    if (newSub.isCompany) {
      notify({
        type: 'success',
        title: 'Company created',
        text: 'Company created successfully'
      })
    } else {
      notify({
        type: 'success',
        title: 'Subcontractor created',
        text: 'Subcontractor created successfully'
      })
    }
    $router.push({ name: 'ShowSubcontractor', params: { id: newSub.id } })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error',
      text: error.message || 'An error occurred while trying to create the subcontractor'
    })
  }
})
</script>
